<script setup>
import { computed } from 'vue';

import MessageMeta from '../MessageMeta.vue';
import CaptainGenerationDetails from '../CaptainGenerationDetails.vue';

import { emitter } from 'shared/helpers/mitt';
import { useMessageContext } from '../provider.js';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_VARIANTS, ORIENTATION, SENDER_TYPES } from '../constants';

const props = defineProps({
  hideMeta: { type: Boolean, default: false },
});

const {
  variant,
  orientation,
  inReplyTo,
  shouldGroupWithNext,
  id,
  sender,
  senderType,
} = useMessageContext();
const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const isCaptainMessage = computed(
  () =>
    (sender.value?.type ?? senderType.value) === SENDER_TYPES.CAPTAIN_ASSISTANT
);

const metaColorClass = computed(() =>
  variant.value === MESSAGE_VARIANTS.PRIVATE
    ? 'text-n-amber-12/50'
    : 'text-n-slate-11'
);

const emailMetaClass = computed(() =>
  variant.value === MESSAGE_VARIANTS.EMAIL ? 'px-3 pb-3' : ''
);

const varaintBaseMap = {
  [MESSAGE_VARIANTS.AGENT]: 'bg-n-solid-blue text-n-slate-12',
  [MESSAGE_VARIANTS.PRIVATE]:
    'bg-n-solid-amber text-n-amber-12 [&_.prosemirror-mention-node]:font-semibold',
  [MESSAGE_VARIANTS.USER]: 'bg-n-slate-4 text-n-slate-12',
  [MESSAGE_VARIANTS.ACTIVITY]: 'bg-n-alpha-1 text-n-slate-11 text-sm',
  [MESSAGE_VARIANTS.BOT]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.TEMPLATE]: 'bg-n-solid-iris text-n-slate-12',
  [MESSAGE_VARIANTS.ERROR]: 'bg-n-ruby-4 text-n-ruby-12',
  [MESSAGE_VARIANTS.EMAIL]: 'w-full',
  [MESSAGE_VARIANTS.UNSUPPORTED]:
    'bg-n-solid-amber/70 border border-dashed border-n-amber-12 text-n-amber-12',
};

const orientationMap = {
  [ORIENTATION.LEFT]:
    'left-bubble rounded-xl ltr:rounded-bl-sm rtl:rounded-br-sm',
  [ORIENTATION.RIGHT]:
    'right-bubble rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm',
  [ORIENTATION.CENTER]: 'rounded-md',
};

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const messageClass = computed(() => {
  const classToApply = [varaintBaseMap[variant.value]];

  if (variant.value !== MESSAGE_VARIANTS.ACTIVITY) {
    classToApply.push(orientationMap[orientation.value]);
  } else {
    classToApply.push('rounded-lg');
  }

  return classToApply;
});

const scrollToMessage = () => {
  const { conversationDisplayId, id: replyMessageId } = inReplyTo.value;

  // Citação de mensagem de outro ticket: navega até lá em vez de rolar a
  // conversa atual (que não tem essa mensagem carregada). Não muda o status
  // do ticket antigo — só abre pra visualização, igual a qualquer link direto.
  if (conversationDisplayId) {
    router.push({
      name: 'inbox_conversation',
      params: {
        accountId: route.params.accountId,
        conversation_id: conversationDisplayId,
      },
      query: { messageId: replyMessageId },
    });
    return;
  }

  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
    messageId: replyMessageId,
  });
};

const shouldShowMeta = computed(
  () =>
    !props.hideMeta &&
    !shouldGroupWithNext.value &&
    variant.value !== MESSAGE_VARIANTS.ACTIVITY
);

// SuperTVGo: mídia do lead grava a TRANSCRIÇÃO em `content` (pro Typebot
// ler), não a fala dele. Se a mensagem citada tem anexo, o anexo é o que ela
// "disse" de verdade — mostra ele, nunca a transcrição, mesmo quando `content`
// também está preenchido.
const replyToPreviewAttachment = computed(() => {
  if (!inReplyTo) return null;
  const { attachments } = inReplyTo.value;
  return attachments?.length ? attachments[0] : null;
});

const replyToPreviewFileType = computed(() => {
  const attachment = replyToPreviewAttachment.value;
  if (!attachment) return null;
  return attachment.fileType ?? attachment.file_type;
});

const replyToPreviewThumbnail = computed(() => {
  const attachment = replyToPreviewAttachment.value;
  if (!attachment || replyToPreviewFileType.value !== 'image') return null;
  return attachment.dataUrl ?? attachment.data_url ?? null;
});

const replyToPreview = computed(() => {
  if (!inReplyTo) return '';

  const { content } = inReplyTo.value;

  if (replyToPreviewFileType.value) {
    return t(`CHAT_LIST.ATTACHMENTS.${replyToPreviewFileType.value}.CONTENT`);
  }
  if (content) return new MessageFormatter(content).formattedMessage;

  return t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND');
});
</script>

<template>
  <div
    class="text-sm min-w-0"
    :class="[
      messageClass,
      {
        'max-w-lg': variant !== MESSAGE_VARIANTS.EMAIL,
      },
    ]"
  >
    <div
      v-if="inReplyTo"
      class="p-2 -mx-1 mb-2 rounded-lg cursor-pointer bg-n-alpha-black1"
      @click="scrollToMessage"
    >
      <div
        v-if="inReplyTo.conversationDisplayId"
        class="text-xs text-n-slate-11 mb-0.5"
      >
        {{ t('CONVERSATION.REPLY_FROM_ANOTHER_CONVERSATION') }}
      </div>
      <div class="flex gap-2 items-center min-w-0">
        <img
          v-if="replyToPreviewThumbnail"
          :src="replyToPreviewThumbnail"
          class="object-cover w-8 h-8 rounded shrink-0"
        />
        <div
          v-dompurify-html="replyToPreview"
          class="min-w-0 prose prose-bubble line-clamp-2"
        />
      </div>
    </div>
    <slot />
    <template v-if="shouldShowMeta">
      <CaptainGenerationDetails
        v-if="isCaptainMessage"
        :message-id="id"
        class="mt-2"
      >
        <template #meta>
          <MessageMeta :class="[emailMetaClass, metaColorClass]" />
        </template>
      </CaptainGenerationDetails>
      <MessageMeta
        v-else
        :class="[flexOrientationClass, emailMetaClass, metaColorClass]"
        class="mt-2"
      />
    </template>
  </div>
</template>
